import { createContext, ReactNode, useEffect, useState } from "react";
import { useDependencies } from "../hooks/useDependencies";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useBrowserLocalStorage } from "../hooks/useBrowserLocalStorage";
import { NotFoundErrorScreen } from "../routes/NotFoundErrorScreen";
import { LoadingScreen } from "../components/loading/LoadingScreen";
import { useSearchParams } from "../hooks/useSearchParams";

export type UnauthenticatedLocaleContextType = {
  setLocale: (locale: string) => void;
  availableLocales: string[] | null;
  locale: string | null;
};

export const UnauthenticatedLocaleContext = createContext<UnauthenticatedLocaleContextType>(
  {} as UnauthenticatedLocaleContextType,
);

export const UnauthenticatedLocaleProvider = ({ children }: { children: ReactNode }) => {
  const { translationController } = useDependencies();
  const queryClient = useQueryClient();
  const { slug, linkType } = useSearchParams();

  const [storedLocale, setStoredLocale] = useBrowserLocalStorage<string | null>(
    `stored_query_param.locale.${linkType}.${slug}`,
    null,
  );

  const [locale, setLocale] = useState<string | null>(storedLocale);

  const queryFn = async () => {
    const { availableLocales, resolvedLocale } = await translationController.getLocaleConfig(
      slug,
      linkType?.getValue(),
    );
    return { availableLocales, resolvedLocale };
  };

  const { data, isLoading } = useQuery({
    queryKey: ["locale-config", slug, linkType?.getValue()],
    queryFn,
    retry: false,
  });

  useEffect(() => {
    if (!locale && data?.resolvedLocale) {
      setLocale(data.resolvedLocale);
    }
  }, [data?.resolvedLocale]);

  useEffect(() => {
    setStoredLocale(locale);
    queryClient.invalidateQueries({ queryKey: ["translations"] });
  }, [locale]);

  const value = {
    locale: locale,
    availableLocales: data?.availableLocales ?? null,
    setLocale,
  };

  if (isLoading) return <LoadingScreen />;
  if (!locale) return <NotFoundErrorScreen />;
  return (
    <UnauthenticatedLocaleContext.Provider value={value}>
      {children}
    </UnauthenticatedLocaleContext.Provider>
  );
};
